// ABOUTME: ApolloLink that bounds how long a single GraphQL query may stay in flight.
// ABOUTME: Queries only — mutations and file uploads are left to run as long as they need.

import { ApolloLink } from "@apollo/client"
import { Observable } from "@apollo/client/utilities"
import { isQueryOperation } from "./operationKind"
import { mirrorSettling } from "./mirrorSettling"

/**
 * Nothing else bounds a request: the client sets no `timeout`, no `AbortController` and no
 * `fetchOptions`, so a socket that stops answering keeps its query in flight until the tab is
 * reloaded — and the spinner above it spins forever. The drag-and-drop slot got its own watchdog
 * (model/dnd/pendingDndOperation), but that guards one screen; this bounds the traffic.
 *
 * Queries only, deliberately. Two mutations carry files (`userUploadPhoto`, `userActivityUpload`)
 * through the same terminal `UploadHttpLink`, and cutting an upload off on a slow connection would
 * break a feature to fix a rare hang. Mutations are also the operations whose interruption leaves the
 * server in a state we cannot read: they are never retried here precisely because they may not be
 * idempotent (see retryIf in client.ts), so aborting one trades a hung request for an unknown write.
 * A read is safe to cut: worst case it is asked for again.
 *
 * The bound is per attempt, and the link sits below retryLink, so a timed-out read is retried like
 * any other network failure — a dead socket is cured by a fresh one, not by waiting longer. Worst
 * case is therefore three attempts plus backoff rather than one bound; finite, which is the point.
 */
export const DEFAULT_QUERY_TIMEOUT_MS = 60_000

/**
 * Chosen against measurements, not taste. A single `calendarItemsBatch` has been observed at
 * 27 749 ms and the `/trainings` transaction at p95 27 581 ms (MULTI_ATHLETE_CALENDAR_PERF_SPEC §0,
 * §1.1), so a 30 s bound would cut off requests that are merely slow — the same trap the DnD watchdog
 * fell into at first. Sixty seconds is a bit over twice the worst thing ever measured here.
 */
export function createTimeoutLink(timeoutMs: number = DEFAULT_QUERY_TIMEOUT_MS): ApolloLink {
  return new ApolloLink((operation, forward) => {
    if (!isQueryOperation(operation.query)) {
      return forward(operation)
    }

    return new Observable((observer) => {
      let timer: ReturnType<typeof setTimeout> | null = null
      const clear = () => {
        if (timer !== null) {
          clearTimeout(timer)
          timer = null
        }
      }

      const subscription = mirrorSettling(forward(operation), observer, clear)

      timer = setTimeout(() => {
        timer = null
        // Unsubscribe first: that is what releases the concurrency limiter's slot for this query
        // (concurrencyLimitLink's teardown), so the ones queued behind a hung request can start.
        subscription.unsubscribe()
        observer.error(new Error(`GraphQL query "${operation.operationName}" timed out after ${timeoutMs}ms`))
      }, timeoutMs)

      return () => {
        clear()
        subscription.unsubscribe()
      }
    })
  })
}
